import { useCallback, useEffect, useRef } from 'react';
import { localDayKey, msSinceLocalMidnight } from './localDay';

/**
 * Runs a piece of work once per local calendar day, shortly after midnight.
 *
 * Correctness rests on comparing a stored day key, never on a timer firing at
 * the right moment. That is what makes it survive the cases a scheduled
 * `setTimeout` does not: a laptop asleep through midnight, a background tab
 * whose timers the browser throttles, a machine whose clock or timezone is
 * changed, and DST days that are 23 or 25 hours long. No timer here is ever
 * armed for more than a minute.
 */

/** How often the day key is re-checked. Cheap: two `Date` reads. */
const TICK_MS = 60 * 1000;

/**
 * Nobody fires at exactly 00:00:00. The floor keeps clients off the instant the
 * server's own rate job runs; spreading the rest over five minutes turns a
 * fleet-wide spike into a trickle, at no cost anyone can perceive.
 */
const MIDNIGHT_JITTER_MIN_MS = 5 * 1000;
const MIDNIGHT_JITTER_MAX_MS = 5 * 60 * 1000;

/** Inside this window after midnight, a tab is part of the herd. */
const HERD_WINDOW_MS = 10 * 60 * 1000;

/**
 * A tab waking hours later (lid reopened, tab restored) is on its own — there
 * is no herd to spread, so it only gets enough jitter to separate sibling tabs.
 */
const WAKE_JITTER_MS = 5 * 1000;

const RETRY_BACKOFF_MS = [30_000, 120_000, 300_000, 900_000, 1_800_000];
const MAX_ATTEMPTS_PER_DAY = 6;

interface UseDailyRefreshOptions<T> {
  /**
   * Inert while false. Must stay false until the app's own bootstrap has
   * landed, otherwise the first mount would re-fetch what it just received.
   */
  enabled: boolean;
  /** Runs once per day. Must throw on anything it would not trust. */
  fetcher: () => Promise<T>;
  /** Applies the result. Must be safe to call more than once. */
  apply: (payload: T) => void;
  /** Dev-only: exposes `{ lastDayKey, refreshNow }` on `window` under this name. */
  debugName?: string;
}

interface DailyRefreshApi {
  /** The local day whose data is currently applied. */
  lastDayKey: string | null;
  /** Ignores the day check, the jitter and the backoff. For dev and QA. */
  refreshNow: () => Promise<void>;
}

export const useDailyRefresh = <T>({
  enabled,
  fetcher,
  apply,
  debugName,
}: UseDailyRefreshOptions<T>): DailyRefreshApi => {
  const lastDayKeyRef = useRef<string | null>(null);
  const attemptsRef = useRef(0);
  const attemptsDayRef = useRef<string | null>(null);
  const nextAllowedAtRef = useRef(0);
  const inFlightRef = useRef(false);
  const scheduledRef = useRef<number | undefined>(undefined);

  // Latest-value refs, so a new `fetcher`/`apply` identity on every render does
  // not restart the tick and the listeners.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const applyRef = useRef(apply);
  applyRef.current = apply;

  const run = useCallback(async (force: boolean) => {
    const dayKey = localDayKey();
    if (!force && dayKey === lastDayKeyRef.current) return;
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    try {
      const payload = await fetcherRef.current();
      applyRef.current(payload);
      lastDayKeyRef.current = dayKey;
      attemptsRef.current = 0;
      nextAllowedAtRef.current = 0;
    } catch (error) {
      // Deliberately silent: this is a background refresh the user did not ask
      // for, and `lastDayKeyRef` is left untouched so it will be retried.
      attemptsRef.current += 1;
      const backoff =
        RETRY_BACKOFF_MS[
          Math.min(attemptsRef.current - 1, RETRY_BACKOFF_MS.length - 1)
        ];
      nextAllowedAtRef.current = Date.now() + backoff;
      console.warn('[dailyRefresh] refresh failed', error);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const maybeRefresh = useCallback(() => {
    const now = new Date();
    const dayKey = localDayKey(now);

    if (dayKey === lastDayKeyRef.current) return;
    if (scheduledRef.current !== undefined || inFlightRef.current) return;

    // A day that has never been attempted starts with a clean budget.
    if (attemptsDayRef.current !== dayKey) {
      attemptsDayRef.current = dayKey;
      attemptsRef.current = 0;
      nextAllowedAtRef.current = 0;
    }
    if (attemptsRef.current >= MAX_ATTEMPTS_PER_DAY) return;
    if (Date.now() < nextAllowedAtRef.current) return;

    const sinceMidnight = msSinceLocalMidnight(now);
    const delay =
      sinceMidnight < HERD_WINDOW_MS
        ? MIDNIGHT_JITTER_MIN_MS +
          Math.random() * (MIDNIGHT_JITTER_MAX_MS - MIDNIGHT_JITTER_MIN_MS)
        : Math.random() * WAKE_JITTER_MS;

    scheduledRef.current = window.setTimeout(() => {
      scheduledRef.current = undefined;
      void run(false);
    }, delay);
  }, [run]);

  const refreshNow = useCallback(() => run(true), [run]);

  useEffect(() => {
    if (!enabled) return undefined;

    // The payload that enabled this hook already carried today's data, so the
    // first mount records the day rather than fetching it again.
    if (lastDayKeyRef.current === null) lastDayKeyRef.current = localDayKey();

    const onWake = () => {
      // Coming back to the tab, or regaining the network, is new information:
      // drop the timed backoff (the per-day attempt budget still applies).
      nextAllowedAtRef.current = 0;
      maybeRefresh();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onWake();
    };

    const tick = window.setInterval(maybeRefresh, TICK_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);

    return () => {
      window.clearInterval(tick);
      if (scheduledRef.current !== undefined) {
        window.clearTimeout(scheduledRef.current);
        scheduledRef.current = undefined;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [enabled, maybeRefresh]);

  useEffect(() => {
    if (!debugName || process.env.NODE_ENV === 'production') return undefined;
    (window as any)[debugName] = {
      get lastDayKey() {
        return lastDayKeyRef.current;
      },
      /** Pretend the stored day is stale, then let the normal path run. */
      forceStale: () => {
        lastDayKeyRef.current = 'force-stale';
      },
      refreshNow,
    };
    return () => {
      delete (window as any)[debugName];
    };
  }, [debugName, refreshNow]);

  return { lastDayKey: lastDayKeyRef.current, refreshNow };
};
